"use client";

import { useTranslations } from "next-intl";
import { ArrowSquareOut, Globe, MapPin } from "@phosphor-icons/react/dist/ssr";
import AffiliateBadge from "@/components/affiliates/AffiliateBadge";
import AffiliateLogo from "@/components/affiliates/AffiliateLogo";
import { BADGE_TAB_CATEGORY } from "@/lib/badge-styles";
import {
  LIST_CARD, LIST_CARD_ACTION, LIST_CARD_ACTION_COLUMN, LIST_CARD_BODY, LIST_CARD_LINK,
  LIST_CARD_META, LIST_CARD_META_ITEM, LIST_CARD_NAME, LIST_CARD_SUBTITLE, LIST_CARD_TAB, LIST_CARD_WELL,
} from "@/lib/card-styles";
import { formatLocation } from "@/lib/provinces";
import { getCategoryLabel, type DirectoryCategory } from "@/lib/categories";
import { AFFILIATE_REL, affiliateDomain, affiliateHref, type AffiliateLink } from "@/lib/affiliates";
import type { Locale } from "@/lib/locale";
import { AFFILIATE_EVENTS, track } from "@/lib/analytics";

/**
 * An affiliate link in the /search results list. Built on the same recipes as
 * `ProfessionalListCard` (`card-styles.ts`), so the list keeps one rhythm, but
 * it says plainly what it is: an "Affiliate link" tag, the destination domain,
 * a "Visit site" action with an external-link icon, and the commission line in
 * the tag's hover tooltip (`AffiliateBadge`). No rating, price or verified badge: there is no profile.
 */
export default function AffiliateListCard({
  affiliate,
  locale = "en",
  activeCategory = "",
  activeLocation = "",
}: {
  affiliate: AffiliateLink;
  locale?: Locale;
  activeCategory?: string;
  activeLocation?: string;
}) {
  const t = useTranslations("affiliates");
  const domain = affiliateDomain(affiliate.url);
  const tabCategory = (activeCategory && affiliate.categories.includes(activeCategory as DirectoryCategory)
    ? activeCategory
    : affiliate.categories[0]) as DirectoryCategory;
  const byProvince = affiliate.coverage === "provinces" && affiliate.provinceSlugs.length > 0;
  const coverage = affiliate.coverage === "anywhere"
    ? t("anywhere")
    : !byProvince
      ? t("nationwide")
      : formatLocation(
        activeLocation && affiliate.provinceSlugs.includes(activeLocation) ? activeLocation : affiliate.provinceSlugs[0],
        locale,
      );
  const extra = byProvince ? affiliate.provinceSlugs.length - 1 : 0;
  const CoverageIcon = affiliate.coverage === "anywhere" ? Globe : MapPin;

  return (
    <article aria-label={`${affiliate.name}, ${t("label")}`} className={LIST_CARD}>
      <a
        href={affiliateHref(affiliate.id, "search")}
        target="_blank"
        rel={AFFILIATE_REL}
        onClick={() => track(AFFILIATE_EVENTS.click, { id: affiliate.id, surface: "search" })}
        className={LIST_CARD_LINK}
      >
        <div className={LIST_CARD_WELL}>
          <AffiliateLogo
            name={affiliate.name}
            logo={affiliate.logo}
            sizes="(min-width: 1024px) 180px, (min-width: 640px) 200px, 104px"
            className="aspect-[3/4] rounded-control"
            initialClass="text-4xl sm:text-6xl"
          />
          <span className={`${BADGE_TAB_CATEGORY} ${LIST_CARD_TAB}`}>
            {getCategoryLabel(tabCategory, locale)}
          </span>
        </div>

        <div className={LIST_CARD_BODY}>
          <p className={LIST_CARD_META}>
            <span className={LIST_CARD_META_ITEM}>
              <CoverageIcon aria-hidden="true" weight="bold" className="h-13 w-13 shrink-0" />
              {coverage}
              {extra > 0 && <span><span aria-hidden="true" className="mx-3">·</span>+{extra}</span>}
            </span>
            {/* Not LIST_CARD_META_ITEM: that one never shrinks, and a long domain must truncate. */}
            {domain && (
              <span className="flex min-w-0 items-center gap-4 normal-case tracking-normal">
                <span aria-hidden="true">·</span>
                <ArrowSquareOut aria-hidden="true" weight="bold" className="h-13 w-13 shrink-0" />
                <span className="truncate">{domain}</span>
              </span>
            )}
          </p>

          <h2 className={LIST_CARD_NAME}>{affiliate.name}</h2>
          <p className={LIST_CARD_SUBTITLE}>{affiliate.tagline}</p>

          <p className="mt-13 hidden text-sm leading-relaxed text-gray-600 sm:line-clamp-3">
            {affiliate.blurb}
          </p>

          {/* Below lg the third column is gone: tag and action move here. */}
          <div className="mt-8 flex flex-wrap items-center gap-x-13 gap-y-5 lg:hidden">
            <AffiliateBadge placement="aboveEnd" />
            <span className="inline-flex items-center gap-5 whitespace-nowrap text-sm font-medium text-navy">
              {t("visitSite")}
              <ArrowSquareOut aria-hidden="true" weight="bold" className="h-13 w-13" />
            </span>
          </div>
        </div>

        <div className={LIST_CARD_ACTION_COLUMN}>
          <AffiliateBadge placement="belowEnd" />
          <span className={`${LIST_CARD_ACTION} inline-flex items-center gap-5 whitespace-nowrap`}>
            {t("visitSite")}
            <ArrowSquareOut aria-hidden="true" weight="bold" className="h-12 w-12" />
          </span>
        </div>
        <span className="sr-only">{t("opensExternal", { domain })}</span>
      </a>
    </article>
  );
}
